import { getCustomTag, useTranslation } from '@msdk/common'
import { CustomActionButtonAspect, Entity, GenericFieldValue, ViewAcl } from '@msdk/definitions'
import {
  ButtonAlternative,
  ButtonIcon,
  ButtonPrimary,
  ButtonSecondary,
  COLORS,
  IconSelector,
  styled,
  Tooltip,
  useComponentLibrary,
} from '@msdk/ui'
import React from 'react'

import { ViewActionButton } from '../../models/form/view-action-button'
import { ActivateButton } from './Activate'
import { DeleteButton } from './Delete'
import { More } from './More'

const StyledButtonPrimary = styled(ButtonPrimary)`
  gap: 8px;
`

const StyledButtonSecondary = styled(ButtonSecondary)`
  gap: 8px;
`

const StyledButtonIcon = styled(ButtonIcon)`
  color: ${COLORS.primary.main};
`

interface Props {
  actions: ViewActionButton[]
  entityName: string
  viewName: string
  reload: () => Promise<void>
  selectedRows?: Entity[]
  currentFilters: Record<string, GenericFieldValue>
  acl: ViewAcl
  legacyActivateDeleteBehaviour?: boolean
}

function SelectButtonComponent({ aspect }: ViewActionButton) {
  switch (aspect) {
    case CustomActionButtonAspect.Alternative:
      return { ButtonComponent: ButtonAlternative, showLabel: true }
    case CustomActionButtonAspect.Secondary:
      return { ButtonComponent: StyledButtonSecondary, showLabel: true }
    case CustomActionButtonAspect.IconOnly:
      return { ButtonComponent: StyledButtonIcon, showLabel: false }
    default:
      return { ButtonComponent: StyledButtonPrimary, showLabel: true }
  }
}

export function ViewActions(props: Props) {
  const t = useTranslation()
  const componentLibrary = useComponentLibrary()

  const extraProps = {}

  return (
    <>
      {props.legacyActivateDeleteBehaviour && props.currentFilters['@ENTITYSTATUS']?.value === 'INACTIVE' && (
        <ActivateButton reload={props.reload} selectedRows={props.selectedRows ?? []} />
      )}
      {props.legacyActivateDeleteBehaviour &&
        props.currentFilters['@ENTITYSTATUS']?.value === 'INACTIVE' &&
        props.acl.canDelete && <DeleteButton reload={props.reload} selectedRows={props.selectedRows ?? []} />}
      {props.actions.map((a) => {
        const { ButtonComponent, showLabel } = SelectButtonComponent(a)
        const tooltipText = a.tooltip ? t(a.tooltip.idml, a.tooltip.description) : null
        if (componentLibrary === 'nova' && a.aspect === CustomActionButtonAspect.Alternative) {
          Object.assign(extraProps, { variant: 'text' })
        }

        return (
          a.visible && (
            <Tooltip key={a.idml} title={a.disabled && tooltipText ? tooltipText : null} arrow>
              <span>
                <ButtonComponent
                  disabled={a.disabled}
                  onClick={() => a.onClick()}
                  data-test-id={getCustomTag(['view', a.description, 'button'])}
                  {...extraProps}
                >
                  {a.icon && <IconSelector iconName={a.icon} />}
                  {showLabel && t(a.idml, a.description)}
                </ButtonComponent>
              </span>
            </Tooltip>
          )
        )
      })}
      <More entityName={props.entityName} />
    </>
  )
}
